import React, { useContext, useMemo, useRef, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import { RegistrationContext } from '../Registration.context';
import { FlattenedProfileData } from '../../../hooks/registration';
import { Paragraph } from '../../shared/Paragraph';
import { Policies, RegistrationStepComponentProps } from '../types';
import { FieldErrorSummary } from '../../../utils/form';
import { Button } from '../../shared/Button';
import { Typography } from '../../shared/Typography';
import { joinStrings } from '../../../utils/string';
import { AbandonPrompt } from './AbandonPrompt';
import { DefaultFormStep } from './DefaultFormStep';
import { RegistrationAddressFormStep } from './RegistrationAddressFormStep';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { Trans } from '../../shared/Trans';
import { ContentPrompt } from './ContentPrompt';
import './styles';

/**
 * Specific registration form.
 */
export const RegistrationFormStep: React.ComponentType<
  RegistrationStepComponentProps & {
    termsAndConditions?: React.ReactNode;
    privacyPolicy?: React.ReactNode;
  }
> = (formStepProps) => {
  const {
    stepConfig,
    previousStepIndex,
    stepIndex,
    selected,
    formState,
    selectedStep,
    onSelectPreviousStep,
    onCompleteStep,
  } = formStepProps;
  const { t } = useTranslations();
  const [modalValue, setModalValue] = useState<Policies | null>(null);
  const emailErrorRef = useRef<string>();

  const { onCloseAbandonPrompt, abandonPromptOpen, onConfirmAbandon } = useContext(RegistrationContext);

  // Transition direction on enter. Will not slide in on first load
  const translateValue =
    typeof previousStepIndex === 'undefined' ? '0%' : stepIndex > previousStepIndex ? '100%' : '-100%';

  const isFirstLoad = typeof previousStepIndex === 'undefined';

  const springConfig = useMemo(
    () => ({
      reset: selected && !previousStepIndex,

      from: {
        opacity: 0,
        transform: `translateX(${translateValue})`,
      },

      to: {
        opacity: selected ? 1 : 0,
        transform: selected ? 'translateX(0%)' : `translateX(${translateValue})`,
      },

      immediate: !selected || isFirstLoad,
    }),
    [selected, translateValue]
  );

  const stepSpring = useSpring(springConfig);

  const handleFormValidationError = (
    errorSummary: Record<string, FieldErrorSummary>,
    formState: Partial<FlattenedProfileData>
  ): void => {
    const emailErrors = errorSummary.email;

    if (!emailErrors) {
      emailErrorRef.current = undefined;
    } else if (
      formState.email !== emailErrorRef.current &&
      emailErrors.messages.some((m) => m.id === 'account-details-form-email-duplicate')
    ) {
      emailErrorRef.current = formState.email;
    }
  };

  const isTargetStep = (stepName: string): boolean => stepConfig.id.toLowerCase().includes(stepName);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isTargetAbandonPrompt = useMemo(() => selectedStep?.id === stepConfig.id, [selectedStep]);

  const isAddressStep = isTargetStep('address');

  if (stepConfig.submissionStep) {
    // Option not currently available in dialogs so manually applied here.
    stepConfig.submitButtonText = t('register.step.final.join-button.text');
  }

  const TermsAndConditions = (): JSX.Element => (
    <Paragraph>
      <Trans keyProp="register.step.final.age-confirmation">
        <Button
          type="button"
          variant="text"
          className="button-link"
          onClick={(): void => {
            setModalValue(Policies.TermsAndConditions);
          }}
        ></Button>
        <Button
          type="button"
          variant="text"
          className="button-link"
          onClick={(): void => {
            setModalValue(Policies.PrivacyPolicy);
          }}
        ></Button>
      </Trans>
    </Paragraph>
  );

  const AcceptMarketing = (): JSX.Element => <Paragraph>{t('register.step.final.accept-marketing')}</Paragraph>;

  if (stepConfig.fieldSets.some((fs) => fs.fields.some((f) => f.name === 'acceptTerms'))) {
    formStepProps.formFields.acceptTerms.label = (
      <Typography size="sm">
        <TermsAndConditions />
      </Typography>
    );
  }

  if (stepConfig.fieldSets.some((fs) => fs.fields.some((f) => f.name === 'acceptMarketingPreferences'))) {
    formStepProps.formFields.acceptMarketingPreferences.label = (
      <Typography size="sm">
        <AcceptMarketing />
      </Typography>
    );
  }

  return (
    <div
      className={joinStrings([
        'registration-form-step',
        selected && 'registration-form-step--running',
        stepConfig.submissionStep && 'registration-form-step--submission',
      ])}
    >
      <animated.div className={joinStrings(['step-footer', selected && 'step-footer--active'])} style={stepSpring}>
        {isAddressStep ? (
          <RegistrationAddressFormStep
            onSelectPreviousStep={(): void => {
              onSelectPreviousStep && onSelectPreviousStep();
            }}
            onCompleteStep={(): void => {
              onCompleteStep && onCompleteStep(stepIndex, formState);
            }}
            {...formStepProps}
          />
        ) : (
          <DefaultFormStep
            headingLevel={5}
            onFormError={handleFormValidationError}
            onSelectPreviousStep={(): void => {
              onSelectPreviousStep && onSelectPreviousStep();
            }}
            onCompleteStep={(): void => {
              onCompleteStep && onCompleteStep(stepIndex, formState);
            }}
            validationMode="onBlur"
            reValidateMode="onChange"
            {...formStepProps}
          />
        )}
      </animated.div>

      {modalValue && <ContentPrompt modalValue={modalValue} onClose={(): void => setModalValue(null)} />}

      <AbandonPrompt
        show={isTargetAbandonPrompt && abandonPromptOpen}
        onDismiss={onCloseAbandonPrompt}
        title={t('registration.modal.abandon-prompt.title')}
        onConfirmAbandon={onConfirmAbandon}
        currentStep={stepConfig.id}
      />
    </div>
  );
};
